const DESKTOP_BREAKPOINT = 750;
const SWIPE_THRESHOLD = 50;

class SucursalReviewsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.track = this.querySelector('.sucursal-reviews__slides');
    this.slides = Array.from(this.querySelectorAll('.sucursal-reviews__card'));
    this.prevButton = this.querySelector('[data-action="prev"]');
    this.nextButton = this.querySelector('[data-action="next"]');
    this.counterCurrent = this.querySelector('[data-carousel-current]');
    this.counterTotal = this.querySelector('[data-carousel-total]');

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.touchStartX = 0;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onPrevClick = () => this.navigate(-1);
    this.onNextClick = () => this.navigate(1);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onTouchStart = (event) => {
      this.touchStartX = event.changedTouches[0].screenX;
    };
    this.onTouchEnd = (event) => {
      const touchEndX = event.changedTouches[0].screenX;
      const diff = this.touchStartX - touchEndX;

      if (Math.abs(diff) < SWIPE_THRESHOLD) return;
      this.navigate(diff > 0 ? 1 : -1);
    };
    this.onScroll = () => {
      window.requestAnimationFrame(() => this.syncIndexFromScroll());
    };

    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.track.addEventListener('keydown', this.onKeyDown);
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);

    this.update();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.track?.removeEventListener('keydown', this.onKeyDown);
    this.track?.removeEventListener('touchstart', this.onTouchStart);
    this.track?.removeEventListener('touchend', this.onTouchEnd);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  get slidesPerView() {
    return window.innerWidth >= DESKTOP_BREAKPOINT ? 3 : 1;
  }

  get totalPages() {
    return Math.max(1, this.slides.length - this.slidesPerView + 1);
  }

  getSlideOffset(index) {
    const slide = this.slides[index];
    if (!slide) return 0;
    return slide.offsetLeft;
  }

  navigate(direction) {
    let nextIndex = this.currentIndex + direction;

    if (nextIndex < 0) {
      nextIndex = this.totalPages - 1;
    } else if (nextIndex >= this.totalPages) {
      nextIndex = 0;
    }

    this.goTo(nextIndex);
  }

  goTo(index) {
    const boundedIndex = Math.max(0, Math.min(index, this.totalPages - 1));
    this.currentIndex = boundedIndex;

    this.track.scrollTo({
      left: this.getSlideOffset(boundedIndex),
      behavior: this.isReducedMotion ? 'auto' : 'smooth',
    });

    this.updateCounter();
    this.updateNavigationState();
  }

  syncIndexFromScroll() {
    if (!this.track || this.slides.length === 0) return;

    const scrollLeft = this.track.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index < this.totalPages; index += 1) {
      const distance = Math.abs(scrollLeft - this.getSlideOffset(index));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;
      this.updateCounter();
      this.updateNavigationState();
    }
  }

  updateCounter() {
    if (this.counterCurrent) {
      this.counterCurrent.textContent = String(this.currentIndex + 1);
    }

    if (this.counterTotal) {
      this.counterTotal.textContent = String(this.totalPages);
    }
  }

  updateNavigationState() {
    const hideNavigation = this.slides.length <= this.slidesPerView;
    const counter = this.querySelector('.sucursal-reviews__counter');

    this.prevButton?.toggleAttribute('hidden', hideNavigation);
    this.nextButton?.toggleAttribute('hidden', hideNavigation);
    counter?.toggleAttribute('hidden', hideNavigation);
  }

  update() {
    if (this.currentIndex > this.totalPages - 1) {
      this.currentIndex = Math.max(0, this.totalPages - 1);
      this.track.scrollTo({
        left: this.getSlideOffset(this.currentIndex),
        behavior: 'auto',
      });
    }

    this.updateCounter();
    this.updateNavigationState();
  }

  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(1);
    }
  }
}

if (!customElements.get('sucursal-reviews-carousel')) {
  customElements.define('sucursal-reviews-carousel', SucursalReviewsCarousel);
}
